import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import Card from "#/components/shared/card";
import ErrorDisplay from "#/components/shared/ErrorDisplay";
import Input from "#/components/shared/input";
import ThemedSelect from "#/components/shared/themed-select";
import Typography from "#/components/shared/typography";
import { MediaSearchParams } from "#/lib/mediaApi";
import { buildMediaUrl } from "#/lib/mediaUrls";
import { MediaDetailsPanel } from "#/features/media/components/MediaDetailsPanel";
import { MediaTable } from "#/features/media/components/MediaTable";
import { useMediaSearch } from "#/features/media/hooks/useMediaSearch";

type CopyPayload = {
  slug?: string | null;
  url?: string | null;
  name?: string | null;
};

type MediaListFilters = {
  context: MediaSearchParams["context"] | "";
  q: string;
  page: number;
  limit: number;
};

const CONTEXT_OPTIONS = [
  "blog",
  "site",
  "marketing",
  "content",
  "general",
] as const;

function normalizeContext(value: string | null) {
  if (!value) return "";
  return CONTEXT_OPTIONS.includes(value as (typeof CONTEXT_OPTIONS)[number])
    ? (value as (typeof CONTEXT_OPTIONS)[number])
    : "";
}

const DEFAULT_LIMIT = 20;

const pageSizeOptions = [
  { value: "20", label: "20 / page" },
  { value: "50", label: "50 / page" },
  { value: "100", label: "100 / page" },
];

function parseNumber(value: string | null, fallback: number) {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) || parsed <= 0 ? fallback : parsed;
}

export function MediaListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");

  const filters = useMemo<MediaListFilters>(() => {
    const context = normalizeContext(searchParams.get("context"));
    const q = searchParams.get("q") ?? "";
    const page = parseNumber(searchParams.get("page"), 1);
    const limit = parseNumber(searchParams.get("limit"), DEFAULT_LIMIT);

    return { context, q, page, limit };
  }, [searchParams]);


  const queryParams = useMemo<MediaSearchParams>(
    () => ({
      context: filters.context || undefined,
      q: filters.q.trim() || undefined,
      page: filters.page,
      limit: filters.limit,
    }),
    [filters]
  );

  const { data, total, isLoading, isError, error, refetch } = useMediaSearch(
    queryParams
  );

  const selectedItem = useMemo(
    () => data.find((item) => item.id === selectedId) ?? null,
    [data, selectedId]
  );

  const setFilter = useCallback(
    (next: Partial<MediaListFilters>) => {
      const merged = { ...filters, ...next };
      const params = new URLSearchParams();

      if (merged.context) {
        params.set("context", merged.context);
      }

      const trimmedQuery = merged.q.trim();
      if (trimmedQuery) {
        params.set("q", trimmedQuery);
      }

      if (merged.page > 1) {
        params.set("page", merged.page.toString());
      }

      if (merged.limit !== DEFAULT_LIMIT) {
        params.set("limit", merged.limit.toString());
      }

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  const handleSearchSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const trimmed = searchInput.trim();
      setFilter({ q: trimmed, page: 1 });
    },
    [searchInput, setFilter]
  );

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setFilter({ q: "", context: "", page: 1 });
  }, [setFilter]);

  useEffect(() => {
    setSearchInput(filters.q);
  }, [filters.q]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const timer = window.setTimeout(() => {
      if (trimmed === filters.q.trim()) return;
      setFilter({ q: trimmed, page: 1 });
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [searchInput, filters.q, setFilter]);

  const totalPages = Math.max(1, Math.ceil(total / filters.limit));

  const handleCopy = useCallback(async (payload: CopyPayload, format: string) => {
    const url = buildMediaUrl(payload.url ?? payload.slug ?? undefined);
    const altText = payload.name ?? payload.slug ?? "Media asset";
    const content =
      format === "img"
        ? `<img src="${url}" alt="${altText}" />`
        : format === "markdown"
        ? `![${altText}](${url})`
        : url;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(content);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = content;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <Typography variant="h3" className="text-brown-900">
          Media Library
        </Typography>
        <Typography variant="body2" className="text-brown-600">
          Search and copy media reference assets for admin workflows.
        </Typography>
      </div>

      <Card className="space-y-4 p-6">
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr_1fr]">
          <form
            onSubmit={handleSearchSubmit}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <Input
              label="Search"
              id="media-search"
              placeholder="Search by name, slug, or URL"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              fullWidth
            />
            <div className="flex items-center gap-2">
              <ActionButton type="submit" tone="secondary">
                Search
              </ActionButton>
              <ActionButton
                type="button"
                tone="secondary"
                onClick={handleClearFilters}
              >
                Show All
              </ActionButton>
            </div>
          </form>
          <ThemedSelect
            key={`media-context-${filters.context}`}
            label="Context"
            options={[
              { value: "", label: "All" },
              { value: "blog", label: "Blog" },
              { value: "site", label: "Site" },
              { value: "marketing", label: "Marketing" },
              { value: "content", label: "Content" },
              { value: "general", label: "General" },
            ]}
            initialValue={filters.context}
            onChange={(event) =>
              setFilter({ context: normalizeContext(event.target.value), page: 1 })
            }
          />
          <ThemedSelect
            key={`media-limit-${filters.limit}`}
            label="Page size"
            options={pageSizeOptions}
            initialValue={filters.limit.toString()}
            onChange={(event) =>
              setFilter({
                limit: Number(event.target.value) || DEFAULT_LIMIT,
                page: 1,
              })
            }
          />
        </div>
      </Card>

      {isError ? (
        <ErrorDisplay
          title="Unable to load media"
          description={
            error instanceof Error
              ? error.message
              : "We ran into an unexpected error loading the media list."
          }
          actions={
            <ActionButton tone="secondary" onClick={() => refetch()}>
              Retry
            </ActionButton>
          }
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <MediaTable
            items={data}
            isLoading={isLoading}
            selectedId={selectedId}
            onRowClick={(item) => setSelectedId(item.id)}
            onCopyUrl={(item) => handleCopy(item, "url")}
            onCopyImg={(item) => handleCopy(item, "img")}
            onCopyMarkdown={(item) => handleCopy(item, "markdown")}
          />

          <div className="flex items-center justify-between rounded-2xl border border-cream-200 bg-white px-4 py-3">
            <Typography variant="body2" className="text-brown-600">
              Page {filters.page} of {totalPages}
            </Typography>
            <div className="flex gap-2">
              <ActionButton
                size="sm"
                tone="secondary"
                disabled={filters.page <= 1}
                onClick={() => setFilter({ page: filters.page - 1 })}
              >
                Previous
              </ActionButton>
              <ActionButton
                size="sm"
                tone="secondary"
                disabled={filters.page >= totalPages}
                onClick={() => setFilter({ page: filters.page + 1 })}
              >
                Next
              </ActionButton>
            </div>
          </div>
        </div>

        <MediaDetailsPanel
          item={selectedItem}
          onClose={() => setSelectedId(null)}
          onCopyUrl={(item) => handleCopy(item as CopyPayload, "url")}
          onCopyImg={(item) => handleCopy(item as CopyPayload, "img")}
          onCopyMarkdown={(item) =>
            handleCopy(item as CopyPayload, "markdown")
          }
        />
      </div>
    </div>
  );
}

export default MediaListPage;
